import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  computed,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { fromResize } from '@examdojo/core/rxjs';
import katex from 'katex';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { VennDiagramComponent, VennDiagramDisplayConfig, VennDiagramParams } from '../venn-diagram';
import { VENN_OPERATIONS } from './venn-dashboard.model';

const renderLatex = (latex: string): string => katex.renderToString(latex, { throwOnError: false, output: 'html' });

const CARD_EFFECTIVE_WIDTH = 119; // 120px card - 1px border overlap

interface VennDashboardCard {
  readonly notation: string;
  readonly notationHtml: SafeHtml;
  readonly description: string;
  readonly explanation: string;
  readonly exampleHtml: SafeHtml;
  readonly params: VennDiagramParams;
}

export interface VennDashboardParams {
  sets?: [string, string];
  labelU?: string;
  initialIndex?: number | null;
}

const VENN_DASHBOARD_DEFAULTS: Required<VennDashboardParams> = {
  sets: ['A', 'B'],
  labelU: 'U',
  initialIndex: 0,
};

@Component({
  selector: 'dojo-venn-dashboard',
  standalone: true,
  imports: [VennDiagramComponent],
  templateUrl: './venn-dashboard.component.html',
  styleUrl: './venn-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class VennDashboardComponent {
  constructor(private readonly sanitizer: DomSanitizer) {
    this.activeIndex.set(this.initialIndex());

    toObservable(this.gridContainer)
      .pipe(
        filter(Boolean),
        switchMap((el) => fromResize(el.nativeElement, { emitOnStart: true })),
        map((rect) => rect.width),
        distinctUntilChanged(),
        debounceTime(50),
        tap((width) => this.containerWidth.set(width)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly params = input<VennDashboardParams>({});

  private readonly resolvedParams = computed(() => ({ ...VENN_DASHBOARD_DEFAULTS, ...this.params() }));
  readonly sets = computed(() => this.resolvedParams().sets);
  readonly labelU = computed(() => this.resolvedParams().labelU);
  readonly initialIndex = computed(() => this.resolvedParams().initialIndex);

  readonly displayConfig: VennDiagramDisplayConfig = { hoverEnabled: false, largeLabels: true };

  readonly activeIndex = signal<number | null>(null);

  private readonly gridContainer = viewChild<ElementRef<HTMLElement>>('gridContainer');
  private readonly containerWidth = signal(0);

  /** All card + detail data pre-computed once (KaTeX rendering happens here, not on click). */
  readonly cards = computed<VennDashboardCard[]>(() => {
    const [a, b] = this.sets();
    const labelU = this.labelU();

    return VENN_OPERATIONS.map((op) => {
      const notation = op.buildNotation(a, b);
      const example = op.buildExample(a, b);
      const exampleHtml = [...example.setDefinitions, example.resultLatex].map((s) => renderLatex(s)).join(', &nbsp;');

      return {
        notation,
        notationHtml: this.sanitizer.bypassSecurityTrustHtml(renderLatex(notation)),
        description: op.buildDescription(a, b),
        explanation: op.buildExplanation(a, b),
        exampleHtml: this.sanitizer.bypassSecurityTrustHtml(exampleHtml),
        params: {
          sets: [a, b],
          highlighted: op.highlighted.map(([inA, inB]) => ({ [a]: inA, [b]: inB })),
          labelU,
        } satisfies VennDiagramParams,
      };
    });
  });

  readonly cardsPerRow = computed(() => {
    const w = this.containerWidth();
    return w > 0 ? Math.max(1, Math.floor(w / CARD_EFFECTIVE_WIDTH)) : VENN_OPERATIONS.length;
  });

  readonly rows = computed(() => {
    const cards = this.cards();
    const perRow = this.cardsPerRow();
    const result: VennDashboardCard[][] = [];
    for (let i = 0; i < cards.length; i += perRow) {
      result.push(cards.slice(i, i + perRow));
    }
    return result;
  });

  readonly activeRowIndex = computed(() => {
    const idx = this.activeIndex();
    if (idx === null) {
      return null;
    }
    return Math.floor(idx / this.cardsPerRow());
  });

  readonly activeCard = computed(() => {
    const idx = this.activeIndex();
    return idx !== null ? this.cards()[idx] ?? null : null;
  });

  /** Width of the active row in pixels (matches the visual card row). */
  readonly activeRowWidthPx = computed(() => {
    const idx = this.activeIndex();
    if (idx === null) {
      return 0;
    }
    const rowIdx = Math.floor(idx / this.cardsPerRow());
    const cardsInRow = this.rows()[rowIdx]?.length ?? this.cardsPerRow();
    return cardsInRow * CARD_EFFECTIVE_WIDTH + 1;
  });

  /** Arrow left offset relative to the detail panel. */
  readonly arrowLeftPx = computed(() => {
    const idx = this.activeIndex();
    if (idx === null) {
      return 0;
    }
    const posInRow = idx % this.cardsPerRow();
    return posInRow * CARD_EFFECTIVE_WIDTH + 60;
  });

  globalIndex(rowIdx: number, colIdx: number): number {
    return rowIdx * this.cardsPerRow() + colIdx;
  }
}
